import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";

const STORAGE_KEY = "hupla-age-confirmed";

// A blocking, translucent full-screen gate that only lets the user through if
// they confirm they are 18 or older. The confirmation is stored in
// sessionStorage so the gate re-appears in every new browser session
// (compliant with typical alcohol-brand practice).
export default function AgeGate({ children }) {
  const { t } = useT();
  const nav = useNavigate();
  const [confirmed, setConfirmed] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "yes"; }
    catch { return false; }
  });
  const [rejected, setRejected] = useState(false);

  const accept = () => {
    try { sessionStorage.setItem(STORAGE_KEY, "yes"); } catch { /* private mode */ }
    setConfirmed(true);
  };
  const reject = () => setRejected(true);

  return (
    <>
      {children}
      <AnimatePresence>
        {!confirmed && (
          <motion.div
            key="age-gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[300] flex items-center justify-center px-6"
            style={{ background: "rgba(15, 26, 18, 0.92)", backdropFilter: "blur(14px)" }}
            data-testid="age-gate"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="relative w-full max-w-lg bg-[#F7F5EE] text-[#0F1A12] rounded-2xl shadow-2xl p-10 md:p-12 text-center"
            >
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.36em] text-[#B02224] mb-4">
                {t("ageGate.overline")}
              </p>
              <h2 className="font-editorial italic leading-[1.05] text-3xl md:text-4xl mb-4" data-testid="age-gate-title">
                {t("ageGate.title")}
              </h2>
              <p className="text-sm md:text-base text-black/70 leading-[1.6] mb-8 max-w-md mx-auto">
                {rejected ? t("ageGate.rejectedBody") : t("ageGate.body")}
              </p>

              {rejected ? (
                <button
                  onClick={() => nav("/")}
                  data-testid="age-gate-go-home"
                  className="inline-flex items-center justify-center rounded-full bg-[#0F1A12] text-white px-6 py-3 text-sm font-semibold hover:bg-black transition-colors"
                >
                  {t("ageGate.goHome")}
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={accept}
                    data-testid="age-gate-yes"
                    className="inline-flex items-center justify-center rounded-full bg-[#0F1A12] text-white px-6 py-3 text-sm font-semibold hover:bg-black transition-colors"
                  >
                    {t("ageGate.yes")}
                  </button>
                  <button
                    onClick={reject}
                    data-testid="age-gate-no"
                    className="inline-flex items-center justify-center rounded-full border border-black/20 text-[#0F1A12] hover:bg-black/5 px-6 py-3 text-sm font-semibold transition-colors"
                  >
                    {t("ageGate.no")}
                  </button>
                </div>
              )}

              <p className="mt-8 text-[0.7rem] text-black/45 max-w-sm mx-auto">
                {t("ageGate.footer")}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
